<script>
	import { createEventDispatcher, onMount, tick } from "svelte";
	import { Table } from "@sveltestrap/sveltestrap";
	import CustomTableLink from "./CustomTableLink.svelte";
	import CustomTableDate from "./CustomTableDate.svelte";
	import CustomTableActions from "./CustomTableActions.svelte";
	import CustomTableStatus from "./CustomTableStatus.svelte";
	import Pagination from "../CustomPaginate/Pagination.svelte";
	import {
		caseExportEnabled,
		caseExportList,
		totalRecords,
	} from "src/store/store";
	import LoaderContainer from "../Loader/LoaderContainer.svelte";
	import CustomTableMasterData from "./CustomTableMasterData.svelte";
	import CustomTableIndex from "./CustomTableIndex.svelte";
	import queryString from "query-string";
	import CustomTableJoinColumn from "./CustomTableJoinColumn.svelte";
	import CustomTableTruncateTooltip from "./CustomTableTruncateTooltip.svelte";
	import CustomTableCheckBox from "./CustomTableCheckBox.svelte";
	import CustomTableCaseActivities from "./CustomTableCaseActivities.svelte";
	import CustomTableBadge from "./CustomTableBadge.svelte";
	import CustomTableFlaggedValue from "./CustomTableFlaggedValue.svelte";
	import CustomTableStatusDropdown from "./CustomTableStatusDropdown.svelte";
	import CustomTableOptionalLink from "./CustomTableOptionalLink.svelte";
	import CustomTableRowCheckbox from "./CustomTableRowCheckbox.svelte";
	import CustomTableRichText from "./CustomTableRichText.svelte";

	export let columns = [];
	export let rows = [];
	export let emptyMessage = "No data to display";
	export let useNavigation = false;
	export let noHeaders = false;
	export let borderless = false;
	export let hover = true;

	let sortColumn = "";
	let sortDirection = "descending"; // display descending by default

	let wrapperEl;
	let extraPad = 0;

	function onMenuRect(e) {
		const menuRect = e.detail;
		if (!menuRect) {
			extraPad = 0;
			return;
		}
		const wrapRect = wrapperEl.getBoundingClientRect();
		const overflow = menuRect.bottom - (wrapRect.bottom - extraPad);
		extraPad = Math.max(0, Math.ceil(overflow));
	}

	const dispatch = createEventDispatcher();

	onMount(() => {
		const params = queryString.parse(window.location.search);
		if (params.sortCol !== undefined) {
			sortColumn = params.sortCol;
		}
		if (params.sortDir !== undefined) {
			sortDirection = params.sortDir;
		}
	});

	async function toggleSort(column) {
		const url = new URL(window.location.href);

		sortDirection =
			sortDirection === "ascending" ? "descending" : "ascending";
		sortColumn = column;

		await tick();

		dispatch("sort", { column, direction: sortDirection });

		url.searchParams.set("sortCol", sortColumn);
		url.searchParams.set("sortDir", sortDirection);
		window.history.pushState({}, "", url);
	}

	function checkAllCases(e) {
		const checkboxes = document.querySelectorAll(
			'tbody input[type="checkbox"]'
		);
		checkboxes.forEach((checkbox) => {
			checkbox.checked = e.target.checked;
		});
		if (!e.target.checked) {
			caseExportList.set([]);
		} else {
			caseExportList.set(rows.map((row) => row["caseNo"]));
		}
	}

	let columnComponents = {
		link: CustomTableLink,
		actions: CustomTableActions,
		date: CustomTableDate,
		status: CustomTableStatus,
		masterdata: CustomTableMasterData,
		index: CustomTableIndex,
		joinColumn: CustomTableJoinColumn,
		checkbox: CustomTableCheckBox,
		caseActivity: CustomTableCaseActivities,
		badge: CustomTableBadge,
		flagged: CustomTableFlaggedValue,
		statusDropdown: CustomTableStatusDropdown,
		optionalLink: CustomTableOptionalLink,
		rowCheckbox: CustomTableRowCheckbox,
		truncateTooltip: CustomTableTruncateTooltip,
		richText: CustomTableRichText,
	};
</script>

<LoaderContainer>
	<div
		class={borderless ? "" : "table-responsive"}
		bind:this={wrapperEl}
		style="padding-bottom: {extraPad}px;"
	>
		<Table {hover} {borderless}>
			{#if !noHeaders}
				<thead>
					<tr>
						{#each columns as { label, key, type, sortCol, event, width, headerChecked, onHeaderToggle }}
							<th
								style={width ? `width: ${width};` : ""}
								{...sortCol && sortColumn === sortCol
									? { "aria-sort": sortDirection }
									: {}}
							>
								{#if type === "checkbox" && $caseExportEnabled}
									<input
										type="checkbox"
										style="width: 13%"
										name={key}
										aria-label="Select all"
										on:change={(e) => {
											checkAllCases(e);
										}}
									/>
								{:else if type === "rowCheckbox"}
									<input
										type="checkbox"
										name={key}
										aria-label="Select all"
										checked={headerChecked}
										on:change={() =>
											onHeaderToggle && onHeaderToggle()}
									/>
								{/if}
								{#if sortCol}
									<span
										class="d-flex justify-content-between align-items-center gap-2"
									>
										{label}
										<button
											type="button"
											style="background:none;border:none;padding:0;cursor:pointer;"
											on:click={() =>
												toggleSort(sortCol, event)}
											>{#if sortColumn === sortCol && sortDirection === "ascending"}
												<img
													src="/assets/icon-sort-down-thin.svg"
													alt="icon-sort-down"
												/>
											{:else}
												<img
													src="/assets/icon-sort-up-thin.svg"
													alt="icon-sort-up"
												/>
											{/if}
										</button>
									</span>
								{:else}
									{label}
								{/if}
							</th>
						{/each}
					</tr>
				</thead>
			{/if}
			<tbody>
				{#each rows as row, index}
					<tr on:click={() => dispatch("rowClick", row)}>
						{#each columns as { key, type, event, action, options, format, className, width, showTitle, ...rest }}
							<td
								style="max-width: fit-content;{width
									? ` width: ${width};`
									: ''}"
							>
								{#if columnComponents[type]}
									<svelte:component
										this={columnComponents[type]}
										{row}
										{key}
										{event}
										{action}
										{options}
										{format}
										{className}
										{...rest}
										index={index + 1}
										on:menuRect={onMenuRect}
										on:statusChange={(e) =>
											dispatch("statusChange", e.detail)}
									/>
								{:else if className}
									<span
										class={className}
										title={showTitle
											? row[key] || ""
											: undefined}>{row[key] || "-"}</span
									>
								{:else}
									{row[key] || "-"}
								{/if}
							</td>
						{/each}
					</tr>
				{/each}
				{#if rows.length === 0}
					<tr>
						<td
							colspan={columns.length}
							class="text-center empty-message"
						>
							{emptyMessage}
						</td>
					</tr>
				{/if}
			</tbody>
		</Table>
	</div>
	<Pagination totalRecord={$totalRecords} {useNavigation} />
</LoaderContainer>

<style>
	.empty-message {
		font-weight: bold;
		white-space: pre-line;
	}

	:global(.table-borderless td, .table-borderless th, .table-borderless tr) {
		border: none !important;
	}
</style>
